import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LogoMark } from "@/components/AuthShell";
import { Headset, Monitor, History, Download, Users, CreditCard, LogOut, Shield, ShieldCheck, Crown, Activity } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { applyZoom } from "@/lib/display";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useHelpAlerts } from "@/lib/useHelpAlerts";
import { Sidebar2 } from "@/components/sv2/Sidebar2";

const TOP = [
  { to: "/app/support", label: "Support", icon: Headset, id: "rail-support" },
  { to: "/app", end: true, label: "Access", icon: Monitor, id: "rail-access" },
  { to: "/app/rmm", label: "RMM", icon: Activity, id: "rail-rmm", module: "rmm" },
  { to: "/app/history", label: "Historique", icon: History, id: "rail-history" },
];
const BOTTOM = [
  { to: "/app/agents", label: "Déployer", icon: Download, id: "rail-deploy" },
  { to: "/app/technicians", label: "Équipe", icon: Users, id: "rail-team", admin: true },
  { to: "/app/billing", label: "Abonnement", icon: CreditCard, id: "rail-billing", admin: true },
  { to: "/app/admin", label: "Admin", icon: Shield, id: "rail-admin", admin: true },
  { to: "/app/superadmin", label: "Plateforme", icon: Crown, id: "rail-superadmin", superadmin: true },
];

const RailItem = ({ to, end, label, icon: Icon, id, badge }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={id}
    className={({ isActive }) =>
      `flex flex-col items-center gap-1 py-3 text-[13px] transition-colors ${isActive ? "bg-white text-slate-900 shadow-[inset_3px_0_0_#2563EB]" : "text-slate-600 hover:bg-slate-200/70"}`
    }
  >
    <span className="relative"><Icon className="h-6 w-6" strokeWidth={1.6} />
      {badge > 0 && <span className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] rounded-full bg-red-600 text-white text-[11px] font-semibold grid place-items-center px-1" data-testid={`${id}-badge`}>{badge}</span>}
    </span>
    {label}
  </NavLink>
);

const ADMIN_GROUP = ["rail-team", "rail-billing", "rail-admin", "rail-superadmin"];

// ScreenView 1: "Admin" rail entry opens Paramètres, Équipe, Abonnement (+ Plateforme).
const RailAdmin = ({ items, navigate }) => {
  const { pathname } = useLocation();
  const active = items.some((n) => pathname.startsWith(n.to));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="rail-admin-group" className={`w-full flex flex-col items-center gap-1 py-3 text-[13px] outline-none transition-colors ${active ? "bg-white text-slate-900 shadow-[inset_3px_0_0_#2563EB]" : "text-slate-600 hover:bg-slate-200/70"}`}>
          <Shield className="h-6 w-6" strokeWidth={1.6} />Admin
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="end" className="w-52">
        {items.map((n) => <DropdownMenuItem key={n.to} onClick={() => navigate(n.to)} data-testid={n.id}><n.icon className="h-4 w-4 mr-2" /> {n.id === "rail-admin" ? "Paramètres" : n.label}</DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const PaymentBanner = ({ billing, admin }) => (
  <div data-testid="billing-payment-banner" className="px-6 py-2 text-sm flex items-center gap-3 border-b bg-red-50 text-red-700 border-red-200">
    Paiement de l'abonnement refusé{billing.grace_until ? ` : les connexions seront coupées le ${fmtDate(billing.grace_until)} sans mise à jour de la carte.` : "."}
    {admin && <Link to="/app/billing" className="font-semibold underline" data-testid="billing-payment-banner-link">Mettre à jour ma carte</Link>}
  </div>
);

const BillingBanner = ({ billing, admin }) => {
  if (billing?.payment_issue) return <PaymentBanner billing={billing} admin={admin} />;
  if (!billing || billing.state === "active") return null;
  const days = Math.max(0, Math.ceil((new Date(billing.trial_ends_at) - Date.now()) / 86400000));
  const expired = billing.state === "expired";
  return (
    <div data-testid="billing-banner" className={`px-6 py-2 text-sm flex items-center gap-3 border-b ${expired ? "bg-red-50 text-red-700 border-red-200" : "bg-blue-50 text-blue-800 border-blue-100"}`}>
      {expired ? (billing.has_subscription ? "Votre abonnement est arrêté : réabonnez-vous pour vous connecter aux machines." : "Votre essai gratuit est terminé : abonnez-vous pour vous connecter aux machines.") : `Essai gratuit : ${days} jour${days > 1 ? "s" : ""} restant${days > 1 ? "s" : ""} (jusqu'au ${fmtDate(billing.trial_ends_at)}).`}
      {admin && <Link to="/app/billing" className="font-semibold underline" data-testid="billing-banner-link">S'abonner</Link>}
    </div>
  );
};

const Announcement = () => {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/announcement").then(({ data }) => setA(data)).catch(() => {}); }, []);
  if (!a || localStorage.getItem("sv-ann-hidden") === a.id) return null;
  return (
    <div data-testid="announcement-banner" className={`px-6 py-2 text-sm flex items-center gap-3 border-b ${a.level === "warning" ? "bg-amber-50 text-amber-900 border-amber-200" : "bg-sky-50 text-sky-900 border-sky-100"}`}>
      <span className="flex-1">{a.text}</span>
      <button type="button" className="text-xs underline opacity-70 hover:opacity-100" onClick={() => { localStorage.setItem("sv-ann-hidden", a.id); setA(null); }} data-testid="announcement-close">Masquer</button>
    </div>
  );
};

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const admin = user?.role === "admin";
  const help = useHelpAlerts();
  const [layout, setLayout] = useState(null);
  const [mini, setMiniState] = useState(localStorage.getItem("sv2-mini") === "1");
  const setMini = (v) => { localStorage.setItem("sv2-mini", v ? "1" : "0"); setMiniState(v); };
  const [sideSlot, setSideSlot] = useState(null);

  const reloadBilling = useCallback(() => api.get("/billing").then(({ data }) => setBilling(data)).catch(() => {}), []);
  useEffect(() => {
    document.body.classList.add("theme-light");
    api.get("/settings/display").then(({ data }) => { applyZoom(data.zoom); setLayout(data.layout); }).catch(() => setLayout("sv1"));
    const onLayout = (e) => setLayout(e.detail);
    window.addEventListener("sv-layout", onLayout);
    reloadBilling();
    const t = setInterval(reloadBilling, 60000);
    return () => {
      clearInterval(t);
      window.removeEventListener("sv-layout", onLayout);
      document.body.classList.remove("theme-light");
      applyZoom(1);
    };
  }, [reloadBilling]);
  useEffect(() => {
    document.body.classList.toggle("theme-sv2", layout === "sv2");
    return () => document.body.classList.remove("theme-sv2");
  }, [layout]);

  const doLogout = async () => {
    navigate("/", { replace: true });
    await logout();
  };
  const top = TOP.filter((n) => (!n.admin || admin) && (!n.module || !billing?.modules || billing.modules.includes(n.module)));
  const bottom = BOTTOM.filter((n) => (!n.admin || admin) && (!n.superadmin || user?.superadmin));
  const accessBadge = new Set(help.open.map((r) => r.machine_id)).size;
  if (!layout) return <div className="h-screen bg-white" data-testid="app-shell-loading" />;

  return (
    <div className={`h-screen flex overflow-hidden bg-white text-slate-900 ${layout === "sv2" ? "font-[Manrope,sans-serif]" : ""}`} style={{ zoom: "var(--sv-zoom, 1)" }} data-testid="app-shell" data-layout={layout}>
      {layout === "sv2" ? (
        <Sidebar2 top={top} bottom={bottom} user={user} admin={admin} mini={mini} setMini={setMini} setSlot={setSideSlot} onLogout={doLogout} accessBadge={accessBadge} />
      ) : (
      <aside className="w-[92px] flex-shrink-0 bg-[#F1F3F6] border-r border-slate-200 flex flex-col" data-testid="app-rail">
        <div className="h-[72px] bg-[#2563EB] grid place-items-center" title={user?.organization?.name}>
          <LogoMark className="h-10 w-10" />
        </div>
        <nav className="flex-1">{top.map((n) => <RailItem key={n.to} {...n} badge={n.id === "rail-access" ? accessBadge : 0} />)}</nav>
        <nav className="pb-2">
          {bottom.filter((n) => !ADMIN_GROUP.includes(n.id)).map((n) => <RailItem key={n.to} {...n} />)}
          {bottom.some((n) => ADMIN_GROUP.includes(n.id)) && <RailAdmin items={["rail-admin", "rail-team", "rail-billing", "rail-superadmin"].map((id) => bottom.find((n) => n.id === id)).filter(Boolean)} navigate={navigate} />}
        </nav>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-btn" className="mx-auto mb-4 h-11 w-11 rounded-full bg-[#2563EB] text-white font-semibold text-lg">
              {user?.name?.[0]?.toUpperCase()}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end">
            <DropdownMenuLabel>
              <p data-testid="current-user-name">{user?.name}</p>
              <p className="text-xs font-normal text-muted-foreground" data-testid="org-name">{user?.organization?.name} · {admin ? "Administrateur" : "Technicien"}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/app/security")} data-testid="security-menu-item"><ShieldCheck className="h-4 w-4 mr-2" /> Sécurité (2FA)</DropdownMenuItem>
            <DropdownMenuItem onClick={doLogout} data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" /> Déconnexion</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </aside>
      )}
      <main className="flex-1 min-w-0 flex flex-col">
        <Announcement />
        <BillingBanner billing={billing} admin={admin} />
        <div className="flex-1 min-h-0 flex">
          <Outlet context={{ billing, reloadBilling, help, layout, sideSlot, mini }} />
        </div>
      </main>
    </div>
  );
}
